import type netlifyIdentity from 'netlify-identity-widget';

import type {
  CreateResourceFn,
  CreateResourceOptions,
  RequestHelpers,
  MakeRequestFn,
} from '$types/request-response';
import {ResourceType} from '$lib/enums/resources';

import {jsonFetchProvider} from './providers';
import {buildRequestUrl} from './build-url';
import {AUTH_USER_KEY} from '$lib/constants/local-storage';

function getAuthHeader(resourceType?: ResourceType): RequestInit['headers'] {
  const rawUser: string | null = localStorage.getItem(AUTH_USER_KEY);
  const user: netlifyIdentity.User | null = rawUser
    ? JSON.parse(rawUser)
    : null;

  return resourceType === ResourceType.protected && user
    ? {Authorization: `Bearer ${user.token.access_token}`}
    : {};
}

function prepareRequest<TEntity = Record<string, unknown>>({
  provider = jsonFetchProvider,
  options: baseOptions = {},
  ...restOptions
}: CreateResourceOptions<TEntity>): MakeRequestFn {
  const {baseUrl, endpoint, resourceType} = restOptions;
  const makeRequest: MakeRequestFn = function makeRequest(requestOptions) {
    const {
      body,
      options: customOptions = {},
      parameters,
      query,
    } = requestOptions;
    const authHeader = getAuthHeader(resourceType);
    const headers = {
      ...baseOptions.headers,
      ...customOptions.headers,
      ...authHeader,
    };
    const options = body
      ? {
          ...baseOptions,
          ...customOptions,
          headers,
          body: typeof body === 'string' ? body : JSON.stringify(body),
        }
      : {...baseOptions, ...customOptions, headers};
    const url = [baseUrl, buildRequestUrl(endpoint, parameters, query)].join(
      '',
    );

    return provider({options, url});
  };

  return makeRequest;
}

const defaultConfig: CreateResourceOptions<unknown> = {
  baseUrl: '',
  endpoint: '/',
  options: {
    headers: {'Content-Type': 'application/json'},
    credentials: 'omit',
  },
  provider: jsonFetchProvider,
  resourceType: ResourceType.protected,
};

const createResource: CreateResourceFn = function createResource<TEntity>(
  userConfig: Partial<CreateResourceOptions<TEntity>>,
) {
  const config = {
    ...defaultConfig,
    ...userConfig,
  };

  const requestHelpers: RequestHelpers = {
    request(options = {}) {
      const makeRequest = prepareRequest(config);

      return makeRequest(options);
    },

    delete(options = {}) {
      const makeRequest = prepareRequest({
        ...config,
        options: {...config.options, method: 'DELETE'},
      });

      return makeRequest(options);
    },

    get(options = {}) {
      const makeRequest = prepareRequest({
        ...config,
        options: {...config.options, method: 'GET'},
      });

      return makeRequest(options);
    },

    head(options = {}) {
      const makeRequest = prepareRequest({
        ...config,
        options: {...config.options, method: 'HEAD'},
      });

      return makeRequest(options);
    },

    patch(options = {}) {
      const makeRequest = prepareRequest({
        ...config,
        options: {...config.options, method: 'PATCH'},
      });

      return makeRequest(options);
    },

    post(options = {}) {
      const makeRequest = prepareRequest({
        ...config,
        options: {...config.options, method: 'POST'},
      });

      return makeRequest(options);
    },

    put(options = {}) {
      const makeRequest = prepareRequest({
        ...config,
        options: {...config.options, method: 'PUT'},
      });

      return makeRequest(options);
    },
  };

  return {...requestHelpers};
};

export {createResource};
